import React, {createContext, useContext, useMemo, useRef} from 'react';

const noOps = {
  startSection: (name: string): string => name,
  logPoint: (): number => 0,
  endSection: (): void => {},
};

const LineProfilerContext = createContext<{
  startSection: (name: string) => string;
  logPoint: (name: string, message: string, mark?: number) => number;
  endSection: (name: string) => void;
}>(noOps);

export const LineProfilerProvider = ({
  children,
  debugMode = false,
}: {
  children: React.ReactNode;
  debugMode?: boolean;
}) => {
  const sectionRef = useRef<
    Map<
      string,
      {
        startTime: number;
        messages: string[];
      }
    >
  >(new Map());

  const value = useMemo(() => {
    if (!debugMode) {
      return noOps;
    }

    return {
      startSection: (name: string) => {
        const startTime = performance.now();
        if (sectionRef.current.has(name)) {
          console.warn(`Section ${name} already started`);
          return name;
        }
        sectionRef.current.set(name, {
          startTime,
          messages: [],
        });
        return name;
      },
      logPoint: (name: string, message: string, mark?: number) => {
        const time = performance.now();
        const section = sectionRef.current.get(name);
        if (!section) {
          console.warn(`Section ${name} not found`);
          return 0;
        }
        const duration = time - (mark ?? section.startTime);
        section.messages.push(`${message} took: ${duration}ms`);
        return time;
      },
      endSection: (name: string) => {
        const section = sectionRef.current.get(name);
        if (!section) {
          console.warn(`Section ${name} not found`);
          return;
        }
        const activeLogs = section.messages.filter(
          message => !message.includes(' 0ms')
        );

        if (activeLogs.length > 0) {
          console.group(name);
          console.log(
            `Overall ${name} took: ${performance.now() - section.startTime}ms`
          );
          activeLogs.forEach(message => {
            console.log(message);
          });
          console.groupEnd();
        }
        sectionRef.current.delete(name);
      },
    };
  }, [debugMode]);
  return (
    <LineProfilerContext.Provider value={value}>
      {children}
    </LineProfilerContext.Provider>
  );
};

export const useLineProfiler = () => {
  return useContext(LineProfilerContext);
};
